import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Fonts } from 'components/base-ui'
import CreditCardImage from './CreditCardImage'
import { useTranslation } from 'react-i18next'
import { useRemoveCreditCardMutation } from 'graphQL/hatostore/generate/operations'
import ModalConfirm from 'components/base-ui/ModalConfirm'
import { ButtonWrapperStickyBottom, Space, StyledTitle } from './styles'
import { CreditCardType } from 'types/payment'

interface PageRemoveCreditCardProps {
  creditCard: CreditCardType
  vendorID: number
  onBack?: () => void
  onRemoveCreditCardCompleted?: (creditCard: CreditCardType) => void
}

export default function PageRemoveCreditCard({
  creditCard,
  vendorID,
  onBack,
  onRemoveCreditCardCompleted,
}: PageRemoveCreditCardProps) {
  const { t } = useTranslation()

  const [isRequestRemoveCard, setIsRequestRemoveCard] = useState<boolean>(false)
  const [isOpenModalConfirmRemoveCard, setIsOpenModalConfirmRemoveCard] = useState<boolean>(false)

  const [removeCreditCard] = useRemoveCreditCardMutation()

  async function handleRemoveCreditCard() {
    setIsRequestRemoveCard(true)
    try {
      await removeCreditCard({
        variables: {
          vendorID,
          cardID: creditCard.cardID,
        },
      })

      setIsRequestRemoveCard(false)
      onBack && onBack()
      onRemoveCreditCardCompleted && onRemoveCreditCardCompleted(creditCard)
      setIsOpenModalConfirmRemoveCard(false)
    } catch (err) {}
  }

  return (
    <>
      <ModalConfirm
        testId="editCreditCardModalConfirm"
        isOpen={isOpenModalConfirmRemoveCard}
        isLoading={isRequestRemoveCard}
        hideCloseButton={false}
        onRequestClose={() => setIsOpenModalConfirmRemoveCard(false)}
        btnAccept={{ content: t('paymentMethodPage.creditCard.confirmRemove.accept'), onClick: handleRemoveCreditCard }}
        btnCancel={{
          outline: true,
          content: t('paymentMethodPage.creditCard.confirmRemove.cancel'),
          onClick: () => setIsOpenModalConfirmRemoveCard(false),
        }}
      >
        <ModalContainer>
          <ModalTitle>{t('paymentMethodPage.creditCard.confirmRemove.title')}</ModalTitle>
          <Fonts.BaseTitleRegular>{t('paymentMethodPage.creditCard.confirmRemove.description')}</Fonts.BaseTitleRegular>
        </ModalContainer>
      </ModalConfirm>

      <div>
        <StyledTitle>{t('paymentMethodPage.creditCard.detail')}</StyledTitle>
        <TopicCreditCardDetail>{t('paymentMethodPage.creditCard.creditCardType')}</TopicCreditCardDetail>
        <CreditCardTypeWrapper>
          <CreditCardImage creditCardBrand={creditCard.cardBrand} />
          <CreditCardBrandText>{creditCard.cardBrand}</CreditCardBrandText>
        </CreditCardTypeWrapper>
        <TopicCreditCardDetail>{t('paymentMethodPage.creditCard.creditCardDigits')}</TopicCreditCardDetail>
        <div>●●●● ●●●● ●●●● {creditCard.cardLastDigits}</div>
        <TopicCreditCardDetail>{t('paymentMethodPage.creditCard.creditCardHolderName')}</TopicCreditCardDetail>
        <div>{creditCard.cardholderName}</div>
      </div>
      <Space isShow={true} />
      <ButtonWrapperStickyBottom>
        <Button
          md
          outline
          onClick={() => setIsOpenModalConfirmRemoveCard(true)}
          isProcessing={isRequestRemoveCard}
          disabled={isRequestRemoveCard}
        >
          {t('paymentMethodPage.creditCard.remove')}
        </Button>
      </ButtonWrapperStickyBottom>
    </>
  )
}

const CreditCardTypeWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`

const TopicCreditCardDetail = styled(Fonts.SmallTitleRegular)`
  color: ${({ theme }) => theme.colors.baseGrey};
  margin-top: 20px;
`

const CreditCardBrandText = styled.span`
  position: relative;
  top: 2px;
`

const ModalContainer = styled.div`
  text-align: center;
  padding-top: 30px;
  padding-bottom: 30px;
`

const ModalTitle = styled(Fonts.H2)`
  margin-bottom: 10px;
`
